import type { StreamEvent } from '@/features/chat/state/types';

import { ensureIdentifiers } from './session';

export interface ChatStreamRequest {
  message: string;
}

const MAX_BUFFER_BYTES = 1_000_000;

export async function* chatStream(
  baseUrl: string,
  req: ChatStreamRequest,
  signal: AbortSignal,
): AsyncGenerator<StreamEvent, void, void> {
  const { userId, sessionId } = ensureIdentifiers();

  const res = await fetch(`${baseUrl}/v1/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({ user_id: userId, session_id: sessionId, message: req.message }),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`stream open failed: HTTP ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      if (buffer.length > MAX_BUFFER_BYTES) {
        yield {
          event: 'error',
          data: { message: `SSE buffer exceeded ${MAX_BUFFER_BYTES} bytes` },
        };
        return;
      }

      const events = buffer.split('\n\n');
      buffer = events.pop() ?? '';

      for (const raw of events) {
        const parsed = parseEvent(raw);
        if (parsed) yield parsed;
      }
    }
  } finally {
    // releaseLock() alone leaves the TCP connection in CLOSE_WAIT until GC,
    // exhausting the per-host connection limit under rapid abort/retry cycles.
    try {
      await reader.cancel().catch(() => undefined);
    } finally {
      try {
        reader.releaseLock();
      } catch {
        // ignore
      }
    }
  }
}

function parseEvent(raw: string): StreamEvent | null {
  const lines = raw.split('\n');
  let eventName = 'message';
  let dataRaw = '';

  for (const line of lines) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataRaw += line.slice(5).trim();
    }
  }

  if (!dataRaw) return null;

  let data: unknown;
  try {
    data = JSON.parse(dataRaw) as unknown;
  } catch {
    return null;
  }

  switch (eventName) {
    case 'status':
      return { event: 'status', data };
    case 'tool':
      return { event: 'tool', data };
    case 'tool_result':
      return { event: 'tool_result', data };
    case 'token':
      return isTokenPayload(data) ? { event: 'token', data } : null;
    case 'done':
      return isDonePayload(data) ? { event: 'done', data } : { event: 'done' };
    case 'error':
      return { event: 'error', data: isErrorPayload(data) ? data : {} };
    default:
      return null;
  }
}

function isTokenPayload(d: unknown): d is { text: string; partial: boolean } {
  return (
    typeof d === 'object' &&
    d !== null &&
    typeof (d as { text?: unknown }).text === 'string' &&
    typeof (d as { partial?: unknown }).partial === 'boolean'
  );
}

function isDonePayload(d: unknown): d is { has_content?: boolean } {
  if (typeof d !== 'object' || d === null) return false;
  const hasContent = (d as { has_content?: unknown }).has_content;
  return hasContent === undefined || typeof hasContent === 'boolean';
}

function isErrorPayload(d: unknown): d is { message?: string } {
  if (typeof d !== 'object' || d === null) return false;
  const m = (d as { message?: unknown }).message;
  return m === undefined || typeof m === 'string';
}
